/**
 * =============================================================================
 * SPEQ55 RFP Pro - User Management API Client
 * =============================================================================
 *
 * API client functions for user management operations.
 *
 * @module api/users
 * @author SPEQ55
 */

import api from './client';

/**
 * Create a new user account (admin only).
 *
 * @param {Object} userData - New user data
 * @param {string} userData.email - User email
 * @param {string} userData.username - Username
 * @param {string} userData.password - Initial password
 * @param {string} userData.firstName - First name
 * @param {string} userData.lastName - Last name
 * @param {string} userData.role - User role
 * @param {string} [userData.organizationId] - Organization ID (optional)
 * @returns {Promise<Object>} Created user
 */
export const createUser = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

/**
 * Get all users with optional filters.
 *
 * @param {Object} filters - Query filters
 * @param {boolean} [filters.activeOnly] - Only return active users
 * @param {string} [filters.role] - Filter by role
 * @param {string} [filters.organizationId] - Filter by organization
 * @returns {Promise<Object>} Response with users array
 */
export const getAllUsers = async (filters = {}) => {
  const params = {};

  if (filters.activeOnly !== undefined) {
    params.activeOnly = filters.activeOnly;
  }

  if (filters.role) {
    params.role = filters.role;
  }

  if (filters.organizationId) {
    params.organizationId = filters.organizationId;
  }

  const response = await api.get('/users', { params });
  return response.data;
};

/**
 * Get a single user by ID.
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User object
 */
export const getUserById = async (userId) => {
  const response = await api.get(`/users/${userId}`);
  return response.data;
};

/**
 * Update a user's information.
 *
 * @param {string} userId - User ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.role] - New role
 * @param {string} [updates.organizationId] - New organization
 * @param {string} [updates.firstName] - New first name
 * @param {string} [updates.lastName] - New last name
 * @param {boolean} [updates.isActive] - Active status
 * @returns {Promise<Object>} Updated user
 */
export const updateUser = async (userId, updates) => {
  const response = await api.patch(`/users/${userId}`, updates);
  return response.data;
};

/**
 * Deactivate a user account (soft delete).
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Updated user
 */
export const deactivateUser = async (userId) => {
  return updateUser(userId, { isActive: false });
};

/**
 * Reactivate a user account.
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Updated user
 */
export const reactivateUser = async (userId) => {
  return updateUser(userId, { isActive: true });
};

/**
 * Reset a user's password (admin only).
 *
 * @param {string} userId - User ID
 * @param {string} newPassword - New temporary password
 * @returns {Promise<Object>} Success response
 */
export const resetUserPassword = async (userId, newPassword) => {
  const response = await api.post(`/users/${userId}/reset-password`, { newPassword });
  return response.data;
};

/**
 * Permanently delete a user (super_admin only).
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Success response
 */
export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};
